"use client";

import { useState, useCallback } from "react";

export type StepNavigationState = {
  currentStep: number;
  isStepDisabled: boolean;
};

export function useCSVStepNavigation(initialStep: number = 1) {
  const [state, setState] = useState<StepNavigationState>({
    currentStep: initialStep,
    isStepDisabled: false,
  });

  const goToStep = useCallback((step: number) => {
    setState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: Math.max(1, prev.currentStep - 1) }));
  }, []);

  const setStepDisabled = useCallback((disabled: boolean) => {
    setState(prev => ({ ...prev, isStepDisabled: disabled }));
  }, []);

  const reset = useCallback(() => {
    setState({
      currentStep: initialStep,
      isStepDisabled: false,
    });
  }, [initialStep]);

  return {
    ...state,
    goToStep,
    nextStep,
    prevStep,
    setStepDisabled,
    reset,
  };
}

export function useCSVImportModal(initialStep: number = 1) {
  const [open, setOpen] = useState(false);
  const stepNavigation = useCSVStepNavigation(initialStep);

  const handleOpenChange = useCallback(
    (newOpen: boolean, onClose?: () => void) => {
      setOpen(newOpen);
      if (!newOpen) {
        // Reset step navigation when modal closes
        setTimeout(() => {
          stepNavigation.reset();
          onClose?.();
        }, 300);
      }
    },
    [stepNavigation]
  );

  const reset = useCallback(() => {
    setOpen(false);
    stepNavigation.reset();
  }, [stepNavigation]);

  return {
    ...stepNavigation,
    open,
    onOpenChange: handleOpenChange,
    reset,
  };
}
